import {
  Switch,
  Text,
} from '@mantine/core';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  stripSearchParams,
} from '@tanstack/react-router';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import React, {
  useMemo,
  useState,
} from 'react';

import {
  DownloadButton,
} from '#components/ui/button/DownloadButton';
import {
  DataTable,
  type DataTableProps,
} from '#components/ui/table/DataTable';
import {
  PageData,
  QueryVirtualWrapper,
  type QueryVirtualWrapperProps,
} from '#components/ui/table/QueryVirtualWrapper';
import useAuthFetch from '#src/hooks/UseAuthFetch';
import {
  ClientFileApiResponse,
  type ClientFileWithId,
} from '#src/types/schema/ClientFile.schema';
import tableSearchValidator, {
  TableSearchParams,
} from '#src/types/tableSearch';
import {
  formatDateTimeStr,
  resolveObject,
  type CommonClient,
  type ClntcommDocument,
} from '@specia-accountant/shared';
import {
  type ClientWithId,
} from '#src/features/client/Client.schema';



interface LoaderDataResult {
  clients: CommonClient[];
  documents: ClntcommDocument[];
}

const defaultValues: Required<TableSearchParams> = {
  sorting: [],
  filters: [],
  fetchSize: 500,
};

async function fetchData(
  start: number,
  fetchSize: number | undefined,
  _sorting,
  _filters,
) {
  const count = (fetchSize ? fetchSize : defaultValues.fetchSize);
  const url = new URL(`${window.origin}/api/client-communication`);
  url.search = new URLSearchParams({
    count: count!.toString(),
    start: start.toString(),
  }).toString();
  const {
    data,
  } = await useAuthFetch<ClientFileApiResponse>(url, {
    method: 'GET',
  });
  return data;
};

async function queryFn(params) {
  const {
    pageParam = 0,
    fetchSize = defaultValues.fetchSize,
    sorting = defaultValues.sorting,
    filters = defaultValues.filters,
  }: {
    pageParam: number;
    fetchSize: number;
    sorting: Array<any>;
    filters: Array<any>;
  } = params;
  const start = pageParam as number;
  const res = await fetchData(
    start,
    fetchSize,
    sorting,
    filters,
  );
  return res;
}

export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/omni/_omniLayout/requests',
)({
  component: RouteComponent,
  validateSearch: tableSearchValidator,
  search: {
    middlewares: [stripSearchParams(defaultValues), // Strip default search parameter values
    ],
  },
  beforeLoad: async (opts) => {
    const {
      filters,
      sorting,
      fetchSize,
    } = opts.search;
    const queryKey = [
      'client-communication',
      (sorting?.length ? sorting : defaultValues.sorting),
      (filters?.length ? filters : defaultValues.filters),
      (fetchSize ? fetchSize : defaultValues.fetchSize),
    ];
    const clients: Promise<LoaderDataResult['clients']> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'clients',
      ],
    });
    const result = await resolveObject({
      requests,
      clients,
    });
    return result;
  },
  loader: async (opts) => {
    const queryClient = opts.context.queryClient;
    const queryOptions = opts.context.clntcommQueryOptions;
    return queryClient.prefetchQuery(queryOptions);
  },
});

function RouteComponent(): React.ReactNode {
  const fetchSize = 50;
  const cellMarginFormatter = ({
    getValue,
  }) => (
    <Text
      span
      ml="md"
      mb="xs"
      ta="center"
    >
      { `${getValue()}` }
    </Text>
  );
  const cellDateFormatter = ({
    getValue,
  }) => {
    const cellDate: string = getValue();
    return (
      <Text
        span
        ml="md"
        mb="xs"
        ta="center"
      >
        { `${formatDateTimeStr(cellDate)}` }
      </Text>
    );
  };

  const columns = useMemo<ColumnDef<ClientFileWithId>[]>(
    () => [
      {
        id: 'select-col',
        size: 100,
        header: ({
          table,
        }) => (
          <>
            <label>
              <Switch
                checked={ table.getIsAllRowsSelected() }
                onChange={ table.getToggleAllRowsSelectedHandler() }
                color="teal"
                size="lg"
                aria-label="Select all"
                thumbIcon={
                  table.getIsAllRowsSelected()
                    ? (
                        <CheckCircleOutlineIcon
                          aria-label="enabled"
                          sx={ {
                            fontSize: 18,
                            fill: 'teal',
                          } }
                        />
                      )
                    : (
                        <CancelOutlinedIcon
                          aria-label="disabled"
                          sx={ {
                            fontSize: 18,
                            fill: 'red',
                          } }
                        />
                      )
                }
              />
            </label>
          </>
        ),
        cell: ({
          row,
        }) => (
          <label
            className="toggle text-base-content place-self-center"
          >
            <Switch
              checked={ row.getIsSelected() }
              onChange={ row.getToggleSelectedHandler() }
              disabled={ !row.getCanSelect() }
              color="teal"
              size="lg"
              aria-label="Select row"
              thumbIcon={
                row.getIsSelected()
                  ? (
                      <CheckCircleOutlineIcon
                        aria-label="enabled"
                        sx={ {
                          fontSize: 18,
                          fill: 'teal',
                        } }
                      />
                    )
                  : (
                      <CancelOutlinedIcon
                        aria-label="disabled"
                        sx={ {
                          fontSize: 18,
                          fill: 'red',
                        } }
                      />
                    )
              }
            />
          </label>
        ),
      },
      {
        accessorKey: 'client_name',
        header: 'Client',
        size: 300,
        cell: cellMarginFormatter,
        meta: {
          filterVariant: 'text',
        },
      },
      {
        accessorKey: 'document_type',
        header: 'Type',
        size: 200,
        cell: cellMarginFormatter,
        meta: {
          filterVariant: 'text',
        },
      },
      {
        accessorKey: 'filename',
        header: 'File',
        size: 600,
        cell: cellMarginFormatter,
        meta: {
          filterVariant: 'text',
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        cell: cellDateFormatter,
        size: 300,
      },
      {
        id: 'email-col',
        size: 250,
        header: ({
          table,
        }) => {
          return (
            <>
              <Text
                span
              >
                Forward Document
              </Text>
            </>
          );
        },
        cell: ({
          row,
        }) => {
          return (
            <Switch
              checked={ row.original.status === '' }
              onChange={ (evt) => {
                console.debug('Forwarding client communications email', evt);
              } }
              color="teal"
              size="lg"
              aria-label="Document forwarding state"
              thumbIcon={
                row.original.status
                  ? (
                      <MarkEmailReadIcon
                        aria-label="document sent"
                        sx={ {
                          fontSize: 18,
                          fill: 'teal',
                        } }
                      />
                    )
                  : (
                      <ForwardToInboxIcon
                        aria-label="forward document"
                        sx={ {
                          fontSize: 18,
                          fill: 'violet',
                        } }
                      />
                    )
              }
            />
          );
        },
      },
      {
        id: 'download-col',
        size: 100,
        header: ({
          table,
        }) => {
          return (
            <>
              <Text
                span
              >
                Download
              </Text>
            </>
          );
        },
        cell: ({
          row,
        }) => {
          return (
            <DownloadButton
              aria-label="Download document"
              row={ row }
            />
          );
        },
      },
      {
        id: 'view-col',
        size: 100,
        header: ({
          table,
        }) => (
          <>
            <Text
              span
            >
              Preview
            </Text>
          </>
        ),
        cell: ({
          row,
        }) => (
          <Link
            to="/mydigitalworkers/omni/$clientId/$fileName"
            params={ {
              clientId: row.original.client_id,
              fileName: row.original.filename,
            } }
            aria-label="Preview document"
          >
            <PreviewIcon />
          </Link>
        ),
      },
    ],
    [],
  );
  // Define data sorting state and setter function
  const sort = useState<SortingState>([]);
  const [
    sorting,
    setSorting,
  ] = sort;
  // Define data filter state and setter function
  const filters = useState<ColumnFiltersState>([]);
  const [
    columnFilters,
    setColumnFilters,
  ] = filters;
  // Define a data fetching function
  const fetchData = async (
    start: number,
    fetchSize: number,
    _sorting,
  ) => {
    const url = new URL(`${window.origin}/api/client-communication`);
    url.search = new URLSearchParams({
      count: fetchSize.toString(),
      start: start.toString(),
    }).toString();
    const {
      data,
    } = await useAuthFetch<ClientFileApiResponse>(url, {
      method: 'GET',
    });
    return data;
  };
  const getNextPageParam = (
    lastPage,
    _allPages,
    _lastPageParam,
    _allPageParams,
  ) => {
    const lastRow = (lastPage as PageData<ClientFileWithId>).data[-1];
    if (lastRow) {
      console.debug(lastRow);
      const lastRowCreatedAt = (lastRow.created_at.getTime() / 1000);
      return (lastRowCreatedAt || 0);
    } else {
      return 0;
    }
  };
  // Query the api for data as needed
  const query = useInfiniteQuery<ClientFileApiResponse>({
    queryKey: [
      'client-communication',
      sorting, // Refetch when sorting changes
      filters, // Refetch when filters change
      fetchSize,
    ],
    queryFn: async ({
      pageParam = 0,
    }) => {
      const start = pageParam as number;
      const res = await fetchData(
        start,
        fetchSize,
        sorting,
      );
      return res;
    },
    initialPageParam: 0,
    getNextPageParam,
    refetchOnWindowFocus: false,
    throwOnError: true,
    placeholderData: keepPreviousData,
  });
  // Combine all the properties needed to configure the table into a single object
  const tableWrappedProps: QueryVirtualWrapperProps<ClientFileWithId, DataTableProps<ClientFileWithId> > = {
    query,
    sort,
    filters,
    columns,
    fetchSize,
    initialState: {
      columnPinning: {
        left: ['select-col'],
        right: ['download-col'],
      },
    },
    contentComponent: DataTable,
  };

  return (
    <>
      <QueryVirtualWrapper
        { ...tableWrappedProps }
      >
        <button>
          <SaveAltIcon
            sx={ {
              mr: 1,
            } }
          />
          Download Selection
        </button>
      </QueryVirtualWrapper>
    </>
  );
}
